import type { $Fetch } from 'ofetch';
import type {
  Aula, UpsertAulaCommand, ListAulasParams,
  MaterialApoio, UpsertMaterialApoioCommand, ListMateriaisParams,
  Manual, UpsertManualCommand, ListManuaisParams,
  Mensagem, EnviarMensagemCommand,
} from '@/domain/model/Comunicacao';
import type {
  AulaGateway, MaterialApoioGateway, ManualGateway, MensagemGateway,
} from '@/application/port/out/ComunicacaoGateways';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** Aulas ao vivo. Authorities `comunicacao:aula:read|manage`. */
export class HttpAulaGateway implements AulaGateway {
  private readonly path = '/api/v1/aulas-ao-vivo';
  constructor(private readonly client: $Fetch) {}
  async list(p: ListAulasParams): Promise<Aula[]> {
    const params: Record<string, string> = {};
    if (p.vinculoTipo) params['vinculoTipo'] = p.vinculoTipo;
    if (p.vinculoId) params['vinculoId'] = p.vinculoId;
    const res = await this.client<ApiResponse<Aula[]>>(this.path, { method: 'GET', params });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertAulaCommand): Promise<Aula> {
    const res = await this.client<ApiResponse<Aula>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertAulaCommand): Promise<Aula> {
    const res = await this.client<ApiResponse<Aula>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async arquivar(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Materiais de apoio. Authorities `comunicacao:material:read|manage`. */
export class HttpMaterialApoioGateway implements MaterialApoioGateway {
  private readonly path = '/api/v1/materiais-apoio';
  constructor(private readonly client: $Fetch) {}
  async list(p: ListMateriaisParams): Promise<MaterialApoio[]> {
    const params: Record<string, string> = {};
    if (p.filtroTipo) params['filtroTipo'] = p.filtroTipo;
    if (p.filtroId) params['filtroId'] = p.filtroId;
    const res = await this.client<ApiResponse<MaterialApoio[]>>(this.path, { method: 'GET', params });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertMaterialApoioCommand): Promise<MaterialApoio> {
    const res = await this.client<ApiResponse<MaterialApoio>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertMaterialApoioCommand): Promise<MaterialApoio> {
    const res = await this.client<ApiResponse<MaterialApoio>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async arquivar(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Manuais institucionais. Authorities `comunicacao:manual:read|manage`. */
export class HttpManualGateway implements ManualGateway {
  private readonly path = '/api/v1/manuais';
  constructor(private readonly client: $Fetch) {}
  async list(p: ListManuaisParams): Promise<Manual[]> {
    const params: Record<string, string> = {};
    if (p.tipo) params['tipo'] = p.tipo;
    const res = await this.client<ApiResponse<Manual[]>>(this.path, { method: 'GET', params });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertManualCommand): Promise<Manual> {
    const res = await this.client<ApiResponse<Manual>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertManualCommand): Promise<Manual> {
    const res = await this.client<ApiResponse<Manual>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async arquivar(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Mensagens instituição → aluno. Authorities `comunicacao:mensagem:read|manage`. */
export class HttpMensagemGateway implements MensagemGateway {
  private readonly path = '/api/v1/comunicacao/mensagens';
  constructor(private readonly client: $Fetch) {}
  async enviar(cmd: EnviarMensagemCommand): Promise<Mensagem> {
    const res = await this.client<ApiResponse<Mensagem>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async listByAluno(alunoId: string): Promise<Mensagem[]> {
    const res = await this.client<ApiResponse<Mensagem[]>>(this.path, { method: 'GET', params: { alunoId } });
    assertApiSuccess(res); return res.data;
  }
}
